import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@material-ui/core';

import {
  getFreelancerTwoFactor,
  removeFreelancerTwoFactor,
  setFreelancerTwoFactorRequirement,
} from '../../utils/api';
import useSnackbar from '../../utils/useSnackbar';

export const REQUIREMENTS = ['DEFAULT', 'REQUIRED', 'EXEMPT'];

/**
 * Lets an admin require or exempt a freelancer from 2FA, and remove their
 * authenticator app, e.g. after they lost their phone
 */
export default function TwoFactorAdminDialog({ userName, onClose, onChange }) {
  const [t] = useTranslation();
  const { showError, showSuccess } = useSnackbar();
  const [status, setStatus] = useState(null);
  const [requirement, setRequirement] = useState('DEFAULT');
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const showStatus = (data) => {
    setStatus(data);
    setRequirement(data.requirement);
  };

  useEffect(() => {
    if (!userName) {
      return undefined;
    }
    let current = true;
    setStatus(null);
    setLoadFailed(false);
    setConfirmRemove(false);
    getFreelancerTwoFactor(userName)
      .then(({ data }) => {
        if (current) {
          setStatus(data);
          setRequirement(data.requirement);
        }
      })
      .catch(() => {
        if (current) {
          setLoadFailed(true);
        }
      });
    // Ignore a slow answer for a user whose dialog was closed already
    return () => {
      current = false;
    };
  }, [userName]);

  const run = async (request, successText) => {
    setBusy(true);
    try {
      const { data } = await request();
      showStatus(data);
      showSuccess(successText);
      onChange();
    } catch (error) {
      showError(t('adminDashboard.twoFactor.saveFailed'));
    } finally {
      setBusy(false);
      setConfirmRemove(false);
    }
  };

  const save = () =>
    run(
      () => setFreelancerTwoFactorRequirement(userName, requirement),
      t('adminDashboard.twoFactor.saved'),
    );

  const remove = () =>
    run(
      () => removeFreelancerTwoFactor(userName),
      t('adminDashboard.twoFactor.removed'),
    );

  const renderRemove = () => {
    if (!status.enrolled) {
      return (
        <Typography variant="body2" color="textSecondary">
          {t('adminDashboard.twoFactor.notEnrolled')}
        </Typography>
      );
    }
    if (!confirmRemove) {
      return (
        <>
          <Typography variant="body2" gutterBottom>
            {t('adminDashboard.twoFactor.enrolled')}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => setConfirmRemove(true)}
            disabled={busy}
            data-testid="two-factor-admin-remove"
          >
            {t('adminDashboard.twoFactor.remove')}
          </Button>
        </>
      );
    }
    return (
      <>
        <Typography variant="body2" gutterBottom>
          {status.required
            ? t('adminDashboard.twoFactor.removeConfirmRequired', { userName })
            : t('adminDashboard.twoFactor.removeConfirm', { userName })}
        </Typography>
        <Button onClick={() => setConfirmRemove(false)} disabled={busy}>
          {t('common.button.cancel')}
        </Button>
        <Button
          color="secondary"
          variant="contained"
          onClick={remove}
          disabled={busy}
          data-testid="two-factor-admin-remove-confirm"
        >
          {t('adminDashboard.twoFactor.removeConfirmButton')}
        </Button>
      </>
    );
  };

  return (
    <Dialog
      open={!!userName}
      onClose={onClose}
      aria-labelledby="two-factor-admin-title"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="two-factor-admin-title">
        {t('adminDashboard.twoFactor.title', { userName })}
      </DialogTitle>
      <DialogContent>
        {loadFailed && (
          <Typography color="error" data-testid="two-factor-admin-load-failed">
            {t('adminDashboard.twoFactor.loadFailed')}
          </Typography>
        )}
        {!loadFailed && !status && (
          <Box display="flex" justifyContent="center" padding={3}>
            <CircularProgress size={32} />
          </Box>
        )}
        {status && (
          <>
            <Typography variant="subtitle2">
              {t('adminDashboard.twoFactor.requirementTitle')}
            </Typography>
            <RadioGroup
              value={requirement}
              onChange={(event) => setRequirement(event.target.value)}
            >
              {REQUIREMENTS.map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  disabled={busy}
                  control={<Radio color="primary" />}
                  label={
                    <Box paddingY={0.5}>
                      <Typography variant="body2">
                        {t(`adminDashboard.twoFactor.requirement.${option}`)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t(
                          `adminDashboard.twoFactor.requirementHint.${option}`,
                        )}
                      </Typography>
                    </Box>
                  }
                  data-testid={`two-factor-admin-${option}`}
                />
              ))}
            </RadioGroup>
            <Box marginY={2}>
              <Divider />
            </Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('adminDashboard.twoFactor.authenticatorTitle')}
            </Typography>
            {renderRemove()}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.button.close')}</Button>
        <Button
          color="primary"
          variant="contained"
          onClick={save}
          disabled={!status || busy || requirement === status.requirement}
          data-testid="two-factor-admin-save"
        >
          {t('common.button.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

TwoFactorAdminDialog.propTypes = {
  userName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
};

TwoFactorAdminDialog.defaultProps = {
  userName: null,
};
